/**
 * Turn a failed response's payload into the sentence a person reads.
 *
 * The API answers failures in TWO shapes, both live, and the client used to
 * handle only one:
 *
 *   { error: 'incorrect email or password' }                 — /api/auth
 *   { error: { code: 'tenant_suspended', message: '...' } }  — /api/v1, and
 *                                                              /api/auth's own
 *                                                              suspended branch
 *
 * `payload?.error` was passed straight to `new Error(...)`, which coerces.
 * The second shape therefore reached the user as the literal string
 * "[object Object]" — and the case that did it was a SUSPENDED TENANT signing
 * in, which is precisely the moment the message has to be readable, because
 * the correct next action is "contact your provider" and the alternative is a
 * customer resetting a password that was never the problem.
 *
 * Both shapes also carry a machine-readable code, in different places. This
 * returns them together so callers stop reaching into the payload themselves.
 */
export function apiFailure(payload, status, fallback = 'request failed') {
  const err = payload?.error;

  if (typeof err === 'string' && err.trim() !== '') {
    // `code` sits beside `error` in the flat shape.
    return { message: err, code: payload?.code };
  }

  if (err && typeof err === 'object') {
    const message = typeof err.message === 'string' && err.message.trim() !== ''
      ? err.message
      : `${fallback} (${status})`;
    return { message, code: err.code ?? payload?.code };
  }

  // No usable payload: a 502 from the proxy, an HTML error page, a dropped
  // connection. The status is the only honest thing left to say.
  return { message: `${fallback} (${status})`, code: payload?.code };
}
